import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { pollForSignals, updateSignalStatus, Signal } from '../services/signalService'
import toast from 'react-hot-toast'
import { useLanguageStore } from '../store/languageStore'
import { isBackgroundTrafficPaused } from '../services/apiConfig'

const SIGNAL_TYPE_LABELS: Record<string, { ar: string; en: string }> = {
  requestinfo: { ar: 'طلب معلومات', en: 'Information Request' },
  executecommand: { ar: 'تنفيذ أمر', en: 'Execute Command' },
  sendstatus: { ar: 'طلب حالة', en: 'Status Request' },
  notification: { ar: 'إشعار', en: 'Notification' },
  message: { ar: 'رسالة', en: 'Message' },
  custom: { ar: 'إشارة مخصصة', en: 'Custom Signal' },
  unknown: { ar: 'إشارة', en: 'Signal' },
}

/**
 * Hook للاستعلام الدوري عن الإشارات وعرضها
 */
export function useSignalPolling(enabled: boolean = true, interval: number = 15000) {
  const { language } = useLanguageStore()
  const isRTL = language === 'ar'

  const { data: signals = [] } = useQuery<Signal[]>({
    queryKey: ['signals', 'polling'],
    queryFn: pollForSignals,
    enabled,
    retry: 0,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: Math.min(interval, 30000),
    refetchInterval: (query) => {
      if (isBackgroundTrafficPaused()) return false
      return query.state.fetchStatus === 'fetching' ? false : interval
    },
  })

  useEffect(() => {
    if (!signals || signals.length === 0) return

    signals.forEach(async (signal) => {
      const processedKey = `signal_processed_${signal.signalId}`
      if (localStorage.getItem(processedKey)) {
        return
      }

      localStorage.setItem(processedKey, 'true')
      await updateSignalStatus(signal.signalId, 'Delivered')
      displaySignal(signal, isRTL)
      await processSignal(signal, isRTL)
    })
  }, [signals, isRTL])

  return { signals }
}

function getSignalTypeLabel(signalType: string, isRTL: boolean): string {
  const key = (signalType || 'unknown').toLowerCase()
  const labels = SIGNAL_TYPE_LABELS[key] ?? SIGNAL_TYPE_LABELS.unknown
  return isRTL ? labels.ar : labels.en
}

function parsePayload(payload?: string): Record<string, unknown> {
  if (!payload?.trim()) return {}

  try {
    const parsed = JSON.parse(payload)
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : { text: payload }
  } catch {
    return { text: payload }
  }
}

function buildSignalMessage(signal: Signal, payload: Record<string, unknown>, isRTL: boolean): string {
  const text =
    (typeof payload.message === 'string' && payload.message) ||
    (typeof payload.text === 'string' && payload.text) ||
    (typeof payload.title === 'string' && payload.title) ||
    ''

  if (text) return text

  switch ((signal.signalType || '').toLowerCase()) {
    case 'requestinfo':
      return isRTL ? 'تم طلب معلومات من الجهاز' : 'Information requested from device'
    case 'executecommand':
      return typeof payload.command === 'string'
        ? (isRTL ? `الأمر: ${payload.command}` : `Command: ${payload.command}`)
        : (isRTL ? 'تم طلب تنفيذ أمر' : 'Command execution requested')
    case 'sendstatus':
      return isRTL ? 'تم طلب حالة الجهاز' : 'Device status requested'
    case 'custom':
      return isRTL ? 'تم استلام إشارة مخصصة من الإدارة' : 'A custom signal was received from administration'
    default:
      if (Object.keys(payload).length > 0) {
        return Object.entries(payload)
          .map(([key, value]) => `${key}: ${String(value)}`)
          .join('\n')
      }
      return isRTL ? 'لديك إشارة جديدة من نظام الإشارات' : 'You have a new signal from the signal system'
  }
}

function displaySignal(signal: Signal, isRTL: boolean) {
  const payload = parsePayload(signal.payload)
  const title = getSignalTypeLabel(signal.signalType, isRTL)
  const message = buildSignalMessage(signal, payload, isRTL)
  const createdAt = new Date(signal.createdAt).toLocaleString(isRTL ? 'ar-SA' : 'en-US')

  toast(
    (t) => (
      <div className="flex flex-col gap-2 max-w-sm text-start">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-700 whitespace-pre-wrap">{message}</div>
        <div className="text-xs text-gray-500">{createdAt}</div>
        <button
          onClick={() => {
            toast.dismiss(t.id)
            showSignalDetails(signal, isRTL)
          }}
          className="mt-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
        >
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
        </button>
      </div>
    ),
    {
      duration: 10000,
      icon: '📡',
      position: 'top-center',
    }
  )
}

function showSignalDetails(signal: Signal, isRTL: boolean) {
  const payload = parsePayload(signal.payload)
  const typeLabel = getSignalTypeLabel(signal.signalType, isRTL)
  const message = buildSignalMessage(signal, payload, isRTL)
  const createdAt = new Date(signal.createdAt).toLocaleString(isRTL ? 'ar-SA' : 'en-US')

  toast(
    (t) => (
      <div className="flex flex-col gap-2 max-w-md text-start">
        <div className="font-bold text-gray-900">
          {isRTL ? 'تفاصيل الإشارة' : 'Signal Details'}
        </div>
        <div className="text-sm space-y-1 text-gray-800">
          <div><span className="font-medium">{isRTL ? 'النوع:' : 'Type:'}</span> {typeLabel}</div>
          <div><span className="font-medium">{isRTL ? 'المحتوى:' : 'Content:'}</span> {message}</div>
          <div><span className="font-medium">{isRTL ? 'الوقت:' : 'Time:'}</span> {createdAt}</div>
          <div className="text-xs text-gray-500 break-all">
            <span className="font-medium">{isRTL ? 'المعرف:' : 'ID:'}</span> {signal.signalId}
          </div>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="mt-1 self-end px-3 py-1.5 bg-gray-700 text-white rounded text-sm hover:bg-gray-800"
        >
          {isRTL ? 'إغلاق' : 'Close'}
        </button>
      </div>
    ),
    {
      duration: 15000,
      icon: 'ℹ️',
      position: 'top-center',
    }
  )
}

async function processSignal(signal: Signal, isRTL: boolean) {
  try {
    const payload = parsePayload(signal.payload)

    switch ((signal.signalType || '').toLowerCase()) {
      case 'requestinfo': {
        const deviceInfo = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timestamp: new Date().toISOString(),
        }
        await updateSignalStatus(signal.signalId, 'Executed', JSON.stringify(deviceInfo))
        break
      }

      case 'executecommand':
        if (payload.command === 'reload') {
          window.location.reload()
        } else if (payload.command === 'navigate' && typeof payload.path === 'string') {
          window.location.href = payload.path
        }
        await updateSignalStatus(signal.signalId, 'Executed', isRTL ? 'تم التنفيذ' : 'Executed')
        break

      case 'sendstatus': {
        const status = {
          online: navigator.onLine,
          timestamp: new Date().toISOString(),
        }
        await updateSignalStatus(signal.signalId, 'Executed', JSON.stringify(status))
        break
      }

      default:
        await updateSignalStatus(signal.signalId, 'Executed', isRTL ? 'تم الاستلام' : 'Received')
    }
  } catch (error) {
    console.error('Error processing signal:', error)
    await updateSignalStatus(signal.signalId, 'Failed', String(error))
  }
}
